// My Activity Review (approved HTML:
// 02-My-Activity-Review-v6-Orange-Right-Tile.html, SHA-256
// 10569c6f2840d74210d45177a6e583bbddf35454b673dd5fc96985ef130d976d).
//
// Sales hero + period chip, Activity today metrics + response times +
// workday + coaching nudge + work strip, Daily pulse, Earnings card,
// Leaderboard, Live-period metrics strip (orange "System processing"
// tile per v6 variant), History comparison and projection, Order
// attribution table with side column, three dialogs.
//
// R3: hydrated from GET /api/projects/:projectId/my-activity via
// services/api-server/routes/my-activity-regions.mjs. The four slices
// the scoping bot flagged null+note (conversion, customers-helped,
// retention, response-times) now read real data from the aggregator;
// any slice that comes back degraded falls back to "No data yet"
// rather than fabricating a number (feedback_no_fabricated_numbers).
// The screen also calls useResumeOnMount (R2) at mount.
//
// W37b (2026-10-20): two views.
//   * Real session (signed or url-bootstrap, not ?preview= / dev mock):
//     <LiveActivityView /> renders ONLY what the composite returns. GET
//     /api/projects/:p/my-activity answers 200 since W37a (MEASURED with
//     the url-bootstrap session: activity-today touches total 0,
//     customers-helped count 0, earnings-card / leaderboard-section /
//     conversion `{available: false, reason: "NO_COMPENSATION_SOURCE"}`,
//     the other regions degraded DEP_MISSING). The header names the rep
//     from the session (useRep), the compensation regions say "Not
//     available yet (no compensation source)", and no Sarah / Jamie /
//     Michael string exists in this view.
//   * Preview (dev mock, ?preview=): the approved illustrative layout
//     below, unchanged. A test may inject `compositeClientOverride` under
//     the dev mock; that renders the live view too.
//
// Stream decision: the composite stream is SSE and the browser's
// EventSource cannot send the Authorization header, so it 401s on every
// real session (MEASURED W37a). The session client below therefore never
// opens a stream; the hook's fallback poll refreshes the composite
// instead and the pill says "Live updates paused".
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { getReviewReturnTarget } from '../_shared/ReviewState.js';
import { Icon } from '../_shared/IconSprite.js';
import { JAMIE, SARAH } from '../../data/mockData.js';
import {
  ATTRIBUTION_ROWS,
  LEADERBOARD_PERIODS,
  LEADERBOARD_ROWS,
  MONTH_HISTORY,
  PERIOD_DATA,
  formatDollars,
  formatMoney,
  type AttributionRow,
  type PeriodKey,
  type RankPeriod,
} from './mockData.js';
import { useOptionalSession } from '../../auth/session-context.js';
import { isDevMockSession } from '../../auth/mockContext.js';
import type { HubSpotSession } from '../../auth/hubspot-handshake.js';
import { useMyActivityComposite, type UseMyActivityCompositeResult } from '../../hooks/useMyActivityComposite.js';
import { useResumeOnMount } from '../../hooks/useResumeOnMount.js';
import type {
  MyActivityCompositeApi,
  MyActivityComposite,
  MyActivityRegionId,
  MyActivityScope,
  ConversionRegion,
  CustomersHelpedRegion,
  RetentionRegion,
  ResponseTimesRegion,
  ActivityTodayRegion,
  DailyPulseRegion,
  EarningsCardRegion,
  HistoricalViewRegion,
  LeaderboardRegion,
  RegionEnvelope,
} from '../../api/my-activity-composite.js';
import { ResumeStateApi } from '../../api/resume-state.js';
import { RuntimeApiError } from '../../api/runtimeClient.js';
import type { RuntimeApiErrorKind } from '../../api/types.js';
import { defaultFetch } from '../../api/default-fetch.js';
import { useScreenRuntime, readRuntimeBaseUrl } from '../CurrentActionReview/runtime-clients.js';
import { usePreviewFixtureMode } from '../_shared/useAuthenticatedCustomer.js';
import { buildSessionHeaders } from '../_shared/useContactProfile.js';
import { useRep, type Rep } from '../_shared/useRep.js';
import './styles.css';
import './app.css';

// W37b: poll cadence for the composite on real sessions (the stream is
// skipped, see the header comment).
export const LIVE_POLL_MS = 30_000;
export const LIVE_UPDATES_NOTE = 'Live updates paused · counts refresh automatically';
export const NO_COMPENSATION_LABEL = 'Not available yet (no compensation source)';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function kindForStatus(status: number): RuntimeApiErrorKind {
  if (status === 401) return 'AUTH_FAILED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 429) return 'RATE_LIMITED';
  if (status === 400) return 'VALIDATION_ERROR';
  if (status === 504) return 'TIMEOUT';
  return 'SERVER_ERROR';
}

export interface SessionCompositeClientOptions {
  /** Read on every request so a context switch moves X-Customer-Id along. */
  getSession: () => HubSpotSession | null;
  /** '' means same-origin relative (the nginx proxy surface). */
  baseUrl: string;
  fetchImpl?: typeof fetch;
}

/**
 * W37b: composite client bound to the HubSpot session. Sends the same
 * headers as every other same-origin read (buildSessionHeaders, including
 * X-User-Id / X-User-Email on url-bootstrap sessions, which is what the
 * api-server resolves the rep id from), works with or without an
 * injected runtime base URL, and never opens the SSE stream.
 */
export function createSessionCompositeClient({
  getSession,
  baseUrl,
  fetchImpl = defaultFetch,
}: SessionCompositeClientOptions): MyActivityCompositeApi {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return {
    async fetchComposite(
      projectId: string,
      opts: { regions?: MyActivityRegionId[]; scope?: MyActivityScope; signal?: AbortSignal; timeoutMs?: number } = {},
    ): Promise<MyActivityComposite> {
      const session = getSession();
      if (!session) {
        throw new RuntimeApiError({ kind: 'AUTH_FAILED', status: 0, message: 'No HubSpot session', errorCode: 'SESSION_UNAVAILABLE' });
      }
      const params = new URLSearchParams({ scope: opts.scope ?? 'today' });
      if (opts.regions && opts.regions.length > 0) params.set('regions', opts.regions.join(','));
      const url = `${base}/api/projects/${encodeURIComponent(projectId)}/my-activity?${params.toString()}`;
      let response: Response;
      try {
        response = await fetchImpl(url, {
          method: 'GET',
          headers: buildSessionHeaders(session),
          ...(opts.signal ? { signal: opts.signal } : {}),
        });
      } catch (err) {
        if (opts.signal?.aborted) {
          throw new RuntimeApiError({ kind: 'TIMEOUT', status: 0, message: 'request aborted', errorCode: 'ABORTED' });
        }
        throw new RuntimeApiError({
          kind: 'NETWORK_ERROR',
          status: 0,
          message: err instanceof Error ? err.message : 'Network failure',
          errorCode: 'NETWORK_ERROR',
        });
      }
      let body: unknown = null;
      try {
        body = await response.json();
      } catch {
        body = null;
      }
      if (!response.ok) {
        const code = isRecord(body) && typeof body.errorCode === 'string' ? body.errorCode : 'UNEXPECTED';
        throw new RuntimeApiError({
          kind: kindForStatus(response.status),
          status: response.status,
          message: `My activity composite returned ${response.status}`,
          errorCode: code,
          body,
        });
      }
      if (!isRecord(body) || body.result !== 'SUCCESS') {
        throw new RuntimeApiError({
          kind: 'SERVER_ERROR',
          status: response.status,
          message: 'Unexpected composite body shape',
          errorCode: 'UNEXPECTED_BODY',
          body,
        });
      }
      return body as unknown as MyActivityComposite;
    },
    // No stream on real sessions: EventSource cannot carry the bearer.
    subscribeStream(): () => void {
      return () => undefined;
    },
  };
}

type Filter = 'all' | 'credited' | 'pending';
type DialogMode =
  | { kind: 'closed' }
  | { kind: 'order'; order: AttributionRow }
  | { kind: 'earnings' }
  | { kind: 'coaching' };

// Test seams: production uses the HubSpot session to mint the clients.
// Tests inject fakes so no real network is touched. When these props are
// omitted entirely (undefined), the screen builds real session-scoped
// clients via useScreenRuntime(). Pass an explicit `null` to force the
// design-preview fixtures even when a live session is available.
export interface MyActivityReviewScreenProps {
  compositeClientOverride?: MyActivityCompositeApi | null;
  resumeApiOverride?: ResumeStateApi | null;
  projectIdOverride?: string | null;
}

// Human-friendly fallback used when a region came back degraded. The
// standing rule is "feedback_no_fabricated_numbers": a degraded region
// renders this placeholder rather than typing a believable-looking
// number into the UI.
const NO_DATA_PLACEHOLDER = 'No data yet';

function readEnvelope<T>(
  env: RegionEnvelope<unknown> | undefined,
): { data: T | null; degraded: RegionEnvelope<unknown>['degraded'] | undefined } {
  if (!env) return { data: null, degraded: undefined };
  return { data: (env.data as T | null) ?? null, degraded: env.degraded };
}

function formatSeconds(ms: number | null): string {
  if (ms === null || ms === undefined) return NO_DATA_PLACEHOLDER;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} sec`;
  const minutes = Math.floor(s / 60);
  const secs = s - minutes * 60;
  if (minutes < 60) return `${minutes}m ${secs.toString().padStart(2, '0')}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${(minutes - hours * 60).toString().padStart(2, '0')}m`;
}

function formatPercentDelta(current: number | null, baseline: number | null): string | null {
  if (current === null || baseline === null) return null;
  const delta = Math.round((current - baseline) * 100);
  if (delta === 0) return 'Even vs. 7-day average';
  const sign = delta > 0 ? 'up' : 'down';
  return `${sign} ${Math.abs(delta)} percentage points vs. 7-day average`;
}

export function MyActivityReviewScreen(props: MyActivityReviewScreenProps = {}): ReactNode {
  const location = useLocation(), navigate = useNavigate();
  const routeParams = useParams();
  const returnTarget = getReviewReturnTarget(location.state, null);
  const returnToWork = (): void => navigate(returnTarget.path, { state: returnTarget.state });
  const returningToSarah = returnTarget.state.returnCustomerId === SARAH.id;
  const [period, setPeriod] = useState<PeriodKey>('sep');
  const [status, setStatus] = useState<Filter>('all');
  const [rankPeriod, setRankPeriod] = useState<RankPeriod>('today');
  const [dialog, setDialog] = useState<DialogMode>({ kind: 'closed' });

  // R3 data wiring. The composite endpoint returns one envelope per
  // region; each region's `degraded` flag drives whether the UI shows
  // real data or the "No data yet" placeholder. Mock values in this
  // component are only consulted when the aggregator did not supply
  // real data AND the region is not degraded (so we never silently
  // substitute mocks for live data).
  //
  // useOptionalSession returns null when the screen is mounted outside
  // a SessionProvider (e.g. the ReviewReturn tests that only wrap in
  // MemoryRouter + ReviewStateProvider). That is a design-preview
  // mount: no live data, no error.
  const session = useOptionalSession();
  const sessionProjectId =
    session && (session.status === 'ready' || session.status === 'refreshing') && session.session
      ? session.session.context.objectId || null
      : null;
  const paramProjectId = routeParams.customerId ?? routeParams.contactId ?? null;
  const projectId =
    props.projectIdOverride !== undefined
      ? props.projectIdOverride
      : (sessionProjectId || paramProjectId || null);

  // W2A upper lane / W37b: when no explicit override is passed, derive
  // the composite client from the live session. Preview-fixture sessions
  // (dev mock or `?preview=sarah`) get no client, which keeps the
  // illustrative sample copy rendering. Explicit `null` overrides force
  // preview-fixture mode even under a real session (used by the
  // r6-data-wiring tests to prove the preview branch stays reachable).
  // The runtime bundle is still used for the resume-state read.
  const runtime = useScreenRuntime();
  const previewFlag = usePreviewFixtureMode();
  const devMock = isDevMockSession(session?.session ?? null);
  const sessionReady =
    !!session && (session.status === 'ready' || session.status === 'refreshing') && !!session.session;
  const realSession = sessionReady && !previewFlag && !devMock;
  const sessionIdentity = session?.session
    ? `${session.session.portalId}:${session.session.userId}:${session.session.token}`
    : null;
  const sessionClient = useMemo<MyActivityCompositeApi | null>(
    () =>
      realSession
        ? createSessionCompositeClient({
            getSession: () => session?.session ?? null,
            baseUrl: readRuntimeBaseUrl(),
          })
        : null,
    // session is read through the accessor on every request; identity
    // + token key the memo so a refresh re-mints without churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [realSession, sessionIdentity],
  );
  const compositeClient =
    props.compositeClientOverride !== undefined
      ? props.compositeClientOverride
      : sessionClient;
  const repState = useRep();
  const rep: Rep | null = repState.status === 'ready' ? repState.rep : null;
  // W38 (2026-10-20): memoized on the runtime bundle. A fresh
  // ResumeStateApi per render made useResumeOnMount (keyed on `api`)
  // refetch after every state change it caused: MEASURED 58 to 62
  // GET /api/projects/:id/resume-state in ~12 s on the live screen once
  // the runtime base URL was injected (before W38 `runtime` was always
  // null here, so the loop never ran).
  const resumeApiOverride = props.resumeApiOverride;
  const resumeApi = useMemo<ResumeStateApi | null>(
    () =>
      resumeApiOverride !== undefined
        ? resumeApiOverride
        : runtime
          ? new ResumeStateApi(runtime.signed)
          : null,
    [resumeApiOverride, runtime],
  );

  // Resume-on-mount: rehydrate without re-prompting prior decisions.
  // The hook does nothing when api or projectId is null, which is the
  // design-review state.
  useResumeOnMount({ api: resumeApi, projectId });

  // Composite region loader. Idles when no client is injected (the
  // workspace shell wires one; design-review previews with no
  // clientOverride stay on the illustrative mocks).
  const composite = useMyActivityComposite({
    client: compositeClient,
    projectId,
    scope: 'today',
    fallbackPollMs: LIVE_POLL_MS,
  });

  const current = period === 'sep';
  const periodData = PERIOD_DATA[period];
  const rank = LEADERBOARD_PERIODS[rankPeriod];

  const filteredRows = useMemo(
    () => (status === 'all' ? ATTRIBUTION_ROWS : ATTRIBUTION_ROWS.filter((r) => r.status === status)),
    [status],
  );

  const earningsDialogRef = useRef<HTMLDialogElement | null>(null);
  const coachingDialogRef = useRef<HTMLDialogElement | null>(null);
  const orderDialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const refs: Record<DialogMode['kind'], HTMLDialogElement | null> = {
      closed: null,
      earnings: earningsDialogRef.current,
      coaching: coachingDialogRef.current,
      order: orderDialogRef.current,
    };
    (Object.keys(refs) as DialogMode['kind'][]).forEach((kind) => {
      const el = refs[kind];
      if (!el) return;
      const shouldOpen = dialog.kind === kind;
      if (shouldOpen && !el.open) el.showModal();
      if (!shouldOpen && el.open) el.close();
    });
  }, [dialog]);

  const dollars = formatDollars;
  const money = (n: number) => `$${n.toFixed(2)}`;

  // Pull per-region envelopes from the composite. Each `data` is null
  // when the aggregator could not answer; `degraded` carries the reason.
  const activityTodayEnv = readEnvelope<ActivityTodayRegion>(
    composite.regions['activity-today'],
  );
  const conversionEnv = readEnvelope<ConversionRegion>(composite.regions['conversion']);
  const customersHelpedEnv = readEnvelope<CustomersHelpedRegion>(composite.regions['customers-helped']);
  const retentionEnv = readEnvelope<RetentionRegion>(composite.regions['retention']);
  const responseTimesEnv = readEnvelope<ResponseTimesRegion>(composite.regions['response-times']);

  // R6: wire the 4 regions R3 flagged (daily-pulse, earnings-card,
  // leaderboard-section, historical-view) to the composite. Visual copy
  // stays unchanged; each tile acquires a data-testid that encodes
  // whether the slice is live or degraded so downstream tests can prove
  // the aggregator -> screen pipeline is intact without visually
  // replacing example copy.
  const dailyPulseEnv = readEnvelope<DailyPulseRegion>(composite.regions['daily-pulse']);
  const earningsCardEnv = readEnvelope<EarningsCardRegion>(composite.regions['earnings-card']);
  const leaderboardEnv = readEnvelope<LeaderboardRegion>(composite.regions['leaderboard-section']);
  const historicalViewEnv = readEnvelope<HistoricalViewRegion>(composite.regions['historical-view']);
  function liveStatusFor<T>(env: {
    data: T | null;
    degraded: RegionEnvelope<unknown>['degraded'] | undefined;
  }): 'idle' | 'degraded' | 'live' {
    if (!liveWiringActive) return 'idle';
    if (env.degraded) return 'degraded';
    if (env.data === null) return 'idle';
    return 'live';
  }

  const liveWiringActive = compositeClient !== null && composite.status !== 'idle';

  // Leaderboard derived numbers
  const gap = rank.values[1] - rank.values[2];
  const youCredit = rank.values[2];

  // Projection panel
  const projValue = current ? 27630 : periodData.sales;
  const earlierIdx = current ? [4, 3] : [periodData.index - 1];

  // W37b: a real session never renders the illustrative layout, not even
  // while the first composite read is in flight; an injected client under
  // the dev mock renders the live view too (existing data-wiring tests).
  if (realSession || liveWiringActive) {
    return (
      <LiveActivityView
        rep={rep}
        composite={composite}
        returnToWork={returnToWork}
        updatesNote={LIVE_UPDATES_NOTE}
      />
    );
  }

  return (
    <WorkspaceChrome screen="my-activity" rail="my-activity">
      <div className="breadcrumbs">
        <div>
          Workspace
          <span className="slash">/</span>
          <strong>My Activity</strong>
        </div>
        <span className="preview">
          {liveWiringActive ? 'Live data when available' : 'Design preview · All figures and orders are illustrative'}
        </span>
      </div>

      {liveWiringActive && composite.degraded.length > 0 ? (
        <div
          role="status"
          data-testid="my-activity-degraded-banner"
          className="my-activity-degraded-banner"
          style={{
            margin: '0 0 12px',
            padding: '10px 14px',
            background: '#fff5e5',
            border: '1px solid #ffe0b2',
            borderRadius: 8,
            fontSize: 12,
            color: '#8a4d00',
          }}
        >
          <strong>Partial data.</strong>{' '}
          {composite.degraded.length === 1
            ? 'One region is temporarily unavailable:'
            : `${composite.degraded.length} regions are temporarily unavailable:`}{' '}
          {composite.degraded.map((d, i) => (
            <span key={d.region}>
              {i > 0 ? ', ' : ''}
              <code>{d.region}</code>
              <small> ({d.reason})</small>
            </span>
          ))}
        </div>
      ) : null}

      {liveWiringActive && composite.status === 'error' && composite.error ? (
        <div
          role="alert"
          data-testid="my-activity-error-banner"
          style={{
            margin: '0 0 12px',
            padding: '10px 14px',
            background: '#fbeaea',
            border: '1px solid #e8bdbd',
            borderRadius: 8,
            fontSize: 12,
            color: '#8a1f1f',
          }}
        >
          Error loading activity: {composite.error.errorCode || 'UNKNOWN'}
        </div>
      ) : null}

      <section className="sales-hero">
        <div>
          <span className="eyebrow">MY ACTIVITY · {JAMIE.fullName.toUpperCase()}</span>
          <h1>Your work. Your impact.</h1>
          <p>Your conversations, customer outcomes and earnings, all in one place.</p>
        </div>
        <div className="period-chip">
          <label htmlFor="sales-period">VIEW PERIOD</label>
          <select
            id="sales-period"
            value={period}
            onChange={(e) => setPeriod(e.target.value as PeriodKey)}
          >
            <option value="sep">September 2026 · In progress</option>
            <option value="aug">August 2026 · Completed</option>
            <option value="jul">July 2026 · Completed</option>
          </select>
          <span id="asof">
            {current
              ? 'Example snapshot through September 20'
              : 'Completed month · Illustrative data'}
          </span>
        </div>
      </section>

      {current ? (
        <>
          <section id="activity-today">
            <div className="activity-heading">
              <div>
                <span className="eyebrow">TODAY · THROUGH 1:00 PM</span>
                <h2>Good conversations build great results.</h2>
              </div>
              <button className="back-to-work" type="button" onClick={returnToWork}>
                {returningToSarah ? `Return to ${SARAH.firstName} · My work` : 'Return to My work'} &rarr;
              </button>
            </div>
            <div className="activity-metrics">
              {/* Touches card. Pulled from activity-today region when
                   live wiring is active; falls back to the illustrative
                   number so the design preview still renders. */}
              <article className="card" data-region="activity-today">
                <span>CUSTOMER TOUCHES</span>
                {liveWiringActive ? (
                  activityTodayEnv.degraded ? (
                    <>
                      <h2 data-testid="touches-value">{NO_DATA_PLACEHOLDER}</h2>
                      <RegionDegradedNote degraded={activityTodayEnv.degraded} />
                    </>
                  ) : (
                    <>
                      <h2 data-testid="touches-value">{activityTodayEnv.data?.touches.total ?? 0}</h2>
                      <p>
                        {activityTodayEnv.data?.touches.byType.call ?? 0} calls ·{' '}
                        {activityTodayEnv.data?.touches.byType.text ?? 0} texts ·{' '}
                        {activityTodayEnv.data?.touches.byType.email ?? 0} emails
                      </p>
                      <small>Completed rep actions</small>
                    </>
                  )
                ) : (
                  <>
                    <h2>58</h2>
                    <p>18 calls · 26 texts · 14 emails</p>
                    <small>Completed rep actions · Example snapshot</small>
                  </>
                )}
              </article>

              {/* Conversion card. R3 wires real data. */}
              <article className="card" data-region="conversion">
                <span>CONVERSION TODAY</span>
                {liveWiringActive ? (
                  conversionEnv.degraded ? (
                    <>
                      <h2 data-testid="conversion-value">{NO_DATA_PLACEHOLDER}</h2>
                      <RegionDegradedNote degraded={conversionEnv.degraded} />
                    </>
                  ) : (
                    <>
                      <h2 data-testid="conversion-value">
                        {conversionEnv.data?.ratio !== null && conversionEnv.data?.ratio !== undefined
                          ? Math.round(conversionEnv.data.ratio * 100)
                          : NO_DATA_PLACEHOLDER}
                        {conversionEnv.data?.ratio !== null && conversionEnv.data?.ratio !== undefined ? <span>%</span> : null}
                      </h2>
                      {conversionEnv.data?.ratio !== null && conversionEnv.data?.sevenDayAverage !== null ? (
                        <p>
                          <b>{formatPercentDelta(conversionEnv.data?.ratio ?? null, conversionEnv.data?.sevenDayAverage ?? null) ?? ''}</b>
                        </p>
                      ) : null}
                      {conversionEnv.data?.closes !== null && conversionEnv.data?.contactsWorked !== null ? (
                        <small>
                          {conversionEnv.data?.closes} of {conversionEnv.data?.contactsWorked} eligible conversations
                        </small>
                      ) : null}
                    </>
                  )
                ) : (
                  <>
                    <h2>
                      20<span>%</span>
                    </h2>
                    <p>
                      <b>&uarr; 3 percentage points</b> vs. 7-day average
                    </p>
                    <small>4 of 20 eligible conversations · Example definition</small>
                  </>
                )}
              </article>

              {/* Customers helped card. R3 wires real data. */}
              <article className="card" data-region="customers-helped">
                <span>CUSTOMERS HELPED</span>
                {liveWiringActive ? (
                  customersHelpedEnv.degraded ? (
                    <>
                      <h2 data-testid="customers-helped-value">{NO_DATA_PLACEHOLDER}</h2>
                      <RegionDegradedNote degraded={customersHelpedEnv.degraded} />
                    </>
                  ) : (
                    <>
                      <h2 data-testid="customers-helped-value">
                        {customersHelpedEnv.data?.count ?? NO_DATA_PLACEHOLDER}
                      </h2>
                      <p>Distinct customers with completed actions</p>
                      <small>Repeat touches count once per customer</small>
                    </>
                  )
                ) : (
                  <>
                    <h2>24</h2>
                    <p>Distinct customers with completed actions</p>
                    <small>Repeat touches count once per customer</small>
                  </>
                )}
              </article>

              {/* Retention card. R3 wires real data. */}
              <article className="card" data-region="retention">
                <span>RETENTION THIS WEEK</span>
                {liveWiringActive ? (
                  retentionEnv.degraded ? (
                    <>
                      <h2 data-testid="retention-value">{NO_DATA_PLACEHOLDER}</h2>
                      <RegionDegradedNote degraded={retentionEnv.degraded} />
                    </>
                  ) : (
                    <>
                      <h2 data-testid="retention-value">
                        {retentionEnv.data?.confirmed ?? NO_DATA_PLACEHOLDER}{' '}
                        {retentionEnv.data?.confirmed !== null ? <span>confirmed</span> : null}
                      </h2>
                      {retentionEnv.data?.pending !== null || retentionEnv.data?.notRetained !== null ? (
                        <p>
                          {(retentionEnv.data?.pending ?? 0) + (retentionEnv.data?.confirmed ?? 0) + (retentionEnv.data?.notRetained ?? 0)} actions ·{' '}
                          {retentionEnv.data?.pending ?? 0} outcomes pending ·{' '}
                          {retentionEnv.data?.notRetained ?? 0} not retained
                        </p>
                      ) : null}
                      <small>Confirmed outcomes, not just messages sent</small>
                    </>
                  )
                ) : (
                  <>
                    <h2>
                      3 <span>confirmed</span>
                    </h2>
                    <p>8 actions · 3 outcomes pending · 2 not retained</p>
                    <small>Confirmed outcomes, not just messages sent</small>
                  </>
                )}
              </article>
            </div>
            <div className="activity-detail-grid">
              <section className="card" data-region="response-times">
                <div className="card-head">
                  <h3>Your response time today</h3>
                  <span className="small">
                    {liveWiringActive ? 'Median response times' : 'Median · Example data'}
                  </span>
                </div>
                {liveWiringActive && responseTimesEnv.degraded ? (
                  <>
                    <div className="response-rows" data-testid="response-times-degraded">
                      <div>
                        <span>
                          <i className="channel-dot call" /> Calls
                        </span>
                        <strong>{NO_DATA_PLACEHOLDER}</strong>
                      </div>
                      <div>
                        <span>
                          <i className="channel-dot chat" /> Live chat
                        </span>
                        <strong>{NO_DATA_PLACEHOLDER}</strong>
                      </div>
                      <div>
                        <span>
                          <i className="channel-dot text" /> Text
                        </span>
                        <strong>{NO_DATA_PLACEHOLDER}</strong>
                      </div>
                      <div>
                        <span>
                          <i className="channel-dot email" /> Email
                        </span>
                        <strong>{NO_DATA_PLACEHOLDER}</strong>
                      </div>
                    </div>
                    <RegionDegradedNote degraded={responseTimesEnv.degraded} />
                  </>
                ) : liveWiringActive ? (
                  <div className="response-rows">
                    <div>
                      <span>
                        <i className="channel-dot call" /> Calls
                      </span>
                      <strong>{formatSeconds(responseTimesEnv.data?.calls ?? null)}</strong>
                      <small>
                        {responseTimesEnv.data?.counts.calls ?? 0} answered inbound calls
                      </small>
                    </div>
                    <div>
                      <span>
                        <i className="channel-dot chat" /> Live chat
                      </span>
                      <strong>{formatSeconds(responseTimesEnv.data?.chat ?? null)}</strong>
                      <small>{responseTimesEnv.data?.counts.chat ?? 0} first responses</small>
                    </div>
                    <div>
                      <span>
                        <i className="channel-dot text" /> Text
                      </span>
                      <strong>{formatSeconds(responseTimesEnv.data?.text ?? null)}</strong>
                      <small>{responseTimesEnv.data?.counts.text ?? 0} first responses</small>
                    </div>
                    <div>
                      <span>
                        <i className="channel-dot email" /> Email
                      </span>
                      <strong>{formatSeconds(responseTimesEnv.data?.email ?? null)}</strong>
                      <small>{responseTimesEnv.data?.counts.email ?? 0} first responses</small>
                    </div>
                  </div>
                ) : (
                  <div className="response-rows">
                    <div>
                      <span>
                        <i className="channel-dot call" />
                        Calls
                      </span>
                      <strong>24 sec</strong>
                      <small>6 answered inbound calls</small>
                    </div>
                    <div>
                      <span>
                        <i className="channel-dot chat" />
                        Live chat
                      </span>
                      <strong>38 sec</strong>
                      <small>5 first responses</small>
                    </div>
                    <div>
                      <span>
                        <i className="channel-dot text" />
                        Text
                      </span>
                      <strong>3m 12s</strong>
                      <small>12 first responses</small>
                    </div>
                    <div>
                      <span>
                        <i className="channel-dot email" />
                        Email
                      </span>
                      <strong>11m 08s</strong>
                      <small>7 first responses</small>
                    </div>
                  </div>
                )}
                <p className="activity-fine">
                  Calls measure answer time. Messages measure time to first rep response. Not
                  averaged across channels.
                </p>
              </section>
              <section className="card workday-card">
                <div className="card-head">
                  <h3>Your workday</h3>
                  <span className="small">Example · Rep local time</span>
                </div>
                <div className="workday-hours">
                  <div>
                    <span>Started</span>
                    <strong>8:00 AM</strong>
                  </div>
                  <div>
                    <span>Working time</span>
                    <strong>4h 45m</strong>
                  </div>
                  <div>
                    <span>Break time</span>
                    <strong>15m</strong>
                  </div>
                </div>
                <div className="workday-track">
                  <i />
                  <b />
                  <i />
                </div>
                <p className="break-detail">Recorded break · 10:15–10:30 AM</p>
                <div className="day-end">
                  <span>
                    Planned end <strong>5:00 PM</strong>
                  </span>
                  <span>
                    Next break <strong>Not scheduled</strong>
                  </span>
                </div>
                <p className="activity-fine">
                  Schedule and recorded availability supply these times.
                </p>
              </section>
              <section className="coaching-nudge">
                <span className="eyebrow">A SMALL THING TO TRY</span>
                <h3>Ask what success looks like.</h3>
                <p>
                  Two recent conversations have product notes but no recorded customer goal. One
                  question could make the next follow-up more personal.
                </p>
                <button
                  className="coaching-link"
                  id="coaching-evidence"
                  type="button"
                  onClick={() => setDialog({ kind: 'coaching' })}
                >
                  See the context &rarr;
                </button>
                <small>Private coaching · Example signal</small>
              </section>
            </div>
            <div className="activity-work-strip">
              <div>
                <span className="work-avatar">{returningToSarah ? SARAH.initials : <Icon id="work" />}</span>
                <div>
                  <strong>{returningToSarah ? `Currently working with ${SARAH.fullName}` : 'Your current work'}</strong>
                  <p>
                    Your prioritized queue stays in My work. Finish one customer, then move to the
                    next.
                  </p>
                </div>
              </div>
              <button className="back-to-work" type="button" onClick={returnToWork}>
                My work &rarr;
              </button>
              <div className="letter-status">
                <span>END-OF-DAY LETTERS</span>
                <strong>Awaiting workflow definition</strong>
                <small>Completed count appears when connected.</small>
              </div>
            </div>
          </section>

          <section
            className="daily-pulse"
            id="daily-pulse"
            data-live-status={liveStatusFor(dailyPulseEnv)}
            data-testid="daily-pulse-live-status"
          >
            <div className="pulse-heading">
              <div>
                <span className="eyebrow">SALES MOMENTUM</span>
                <h2>Your sales results today.</h2>
              </div>
              <span>Example snapshot · Sep 20, 1:00 PM</span>
            </div>
            <div className="daily-cards">
              <article>
                <span>SALES CREDITED TODAY</span>
                <h2>$1,260</h2>
                <p>
                  <b>&uarr; 28.6%</b> vs. yesterday at 1 PM
                </p>
                <small>Yesterday at 1 PM: $980</small>
              </article>
              <article>
                <span>ORDERS CREDITED TODAY</span>
                <h2>4</h2>
                <p>
                  <b>+1 order</b> vs. yesterday at 1 PM
                </p>
                <small>Average credited order: $315</small>
              </article>
              <article>
                <span>THIS WEEK SO FAR</span>
                <h2>$4,860</h2>
                <p>
                  <b>&uarr; 12.5%</b> vs. the same point last week
                </p>
                <small>Previous comparable total: $4,320</small>
              </article>
              <article className="personal-best">
                <span>YOUR NEXT PERSONAL BEST</span>
                <h2>
                  $420 <small>to match</small>
                </h2>
                <p>
                  Your best day this month: <strong>$1,680</strong>
                </p>
                <div className="best-track">
                  <i />
                </div>
                <small>75% of the way there. Take care of the next customer.</small>
              </article>
            </div>
          </section>

          <section
            className="earnings-card"
            id="earnings-card"
            data-live-status={liveStatusFor(earningsCardEnv)}
            data-testid="earnings-card-live-status"
          >
            <div className="earnings-header">
              <div>
                <span className="eyebrow">YOUR EARNINGS</span>
                <h2>See what your momentum earns.</h2>
              </div>
              <span className="earnings-demo">Illustrative compensation plan</span>
            </div>
            <div className="earnings-grid">
              <div className="earned-total">
                <span>BONUS EARNED THIS PAY PERIOD</span>
                <h2>
                  $921<span>.00</span>
                </h2>
                <p>
                  <i />
                  Calculated by the system
                </p>
                <div className="payout-line">
                  <span>Next payout</span>
                  <strong>
                    Oct 9, 2026 <small>Example</small>
                  </strong>
                </div>
                <small className="earned-period">
                  Example pay period: Sep 1–30 · Through Sep 20
                </small>
              </div>
              <div className="tier-progress">
                <div className="tier-head">
                  <div>
                    <span>CURRENT LEVEL</span>
                    <strong>
                      Tier 2 <b>5%</b>
                    </strong>
                  </div>
                  <span className="tier-arrow">&rarr;</span>
                  <div>
                    <span>NEXT LEVEL</span>
                    <strong>
                      Tier 3 <b>6%</b>
                    </strong>
                  </div>
                </div>
                <h3>
                  <span>$1,580</span> to your next tier
                </h3>
                <p>$18,420 in qualifying sales toward $20,000</p>
                <div
                  className="earnings-track"
                  role="progressbar"
                  aria-label="Qualifying sales toward Tier 3"
                  aria-valuemin={0}
                  aria-valuemax={20000}
                  aria-valuenow={18420}
                >
                  <i />
                </div>
                <div className="tier-labels">
                  <span>$18,420 earned sales</span>
                  <strong>$20,000 · Tier 3</strong>
                </div>
                <button
                  className="earnings-rules"
                  id="open-earnings-rules"
                  type="button"
                  onClick={() => setDialog({ kind: 'earnings' })}
                >
                  How my bonus is calculated &nearr;
                </button>
              </div>
              <div className="tier-reward">
                <span>AT THE NEXT TIER</span>
                <h3>$1,200</h3>
                <p>Total bonus at $20,000 in qualifying sales</p>
                <div className="reward-lift">
                  <strong>+$279</strong>
                  <span>more than your current earned bonus</span>
                </div>
                <small>
                  Illustrative milestone, not yet earned. Includes the additional sales and the
                  higher rate.
                </small>
              </div>
            </div>
            <div className="earnings-foot">
              <span>
                <strong>Pending:</strong> 3 orders · $940 sales awaiting system attribution
              </span>
              <span>Excluded from earned bonus until qualified.</span>
            </div>
          </section>

          <section
            id="leaderboard-section"
            className="card leaderboard"
            data-live-status={liveStatusFor(leaderboardEnv)}
            data-testid="leaderboard-live-status"
          >
            <div className="card-head">
              <div>
                <span className="eyebrow">TEAM LEADERBOARD</span>
                <h3>A little friendly competition.</h3>
              </div>
              <div className="rank-switch" role="group" aria-label="Leaderboard period">
                {(['today', 'week', 'month'] as RankPeriod[]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    data-rank-period={key}
                    aria-pressed={rankPeriod === key}
                    onClick={() => setRankPeriod(key)}
                  >
                    {key === 'today' ? 'Today' : key === 'week' ? 'This week' : 'This month'}
                  </button>
                ))}
              </div>
            </div>
            <div className="leaderboard-content">
              <div className="rank-summary">
                <span className="eyebrow">YOUR POSITION</span>
                <div className="rank-number">
                  #3 <span>of 6</span>
                </div>
                <strong>{JAMIE.fullName}</strong>
                <p>
                  {dollars(youCredit)} in credited sales {rank.context}
                </p>
                <div
                  className="rank-progress"
                  aria-label={`${((youCredit / rank.values[1]) * 100).toFixed(1)} percent of second place's credited sales`}
                >
                  <i style={{ width: `${(youCredit / rank.values[1]) * 100}%` }} />
                </div>
                <small>
                  {dollars(gap)} to match #2 · More than {dollars(gap)} to move ahead
                </small>
              </div>
              <div className="rank-list">
                <div className="rank-row rank-label">
                  <span>RANK / REP</span>
                  <span>CREDITED SALES</span>
                </div>
                {LEADERBOARD_ROWS.map((row, i) => (
                  <div
                    key={row.rank}
                    className={`rank-row${row.you ? ' your-rank' : ''}`}
                    aria-label={row.you ? `${row.name}, your rank: ${row.rank} of 6` : undefined}
                  >
                    <div>
                      <b className="rank-medal">{row.rank}</b>
                      <span>
                        {row.name}
                        {row.you ? <em>YOU</em> : null}
                        <small>{row.subtitle}</small>
                      </span>
                    </div>
                    <strong>{dollars(rank.values[i])}</strong>
                  </div>
                ))}
              </div>
            </div>
            <div className="leaderboard-note">
              <span id="ranking-period-label">{rank.label}</span> · Illustrative standings. All
              reps use the same period and credited-sales measure. Processing orders are excluded.
              Equal totals share a rank.
            </div>
          </section>

          <div id="live-period-view">
            <div className="sales-metrics">
              <article className="card">
                <span>CREDITED SALES</span>
                <h2>
                  $18,420<span>.00</span>
                </h2>
                <p>Confirmed attribution · Example</p>
                <i className="metric-bar blue" />
              </article>
              <article className="card">
                <span>CREDITED ORDERS</span>
                <h2>64</h2>
                <p>Orders credited to Jamie · Example</p>
                <i className="metric-bar teal" />
              </article>
              <article className="card">
                <span>AVERAGE CREDITED ORDER</span>
                <h2>
                  $287<span>.81</span>
                </h2>
                <p>Credited sales ÷ credited orders</p>
                <i className="metric-bar purple" />
              </article>
              <article className="card pending-metric">
                <span>SYSTEM PROCESSING</span>
                <h2>
                  3 <span>orders</span>
                </h2>
                <p>$940.00 · Excluded from credited totals</p>
                <i className="metric-bar orange" />
              </article>
            </div>
          </div>
        </>
      ) : (
        <section
          id="historical-view"
          className="card historical-view"
          data-live-status={liveStatusFor(historicalViewEnv)}
          data-testid="historical-view-live-status"
        >
          <h2>{periodData.month} 2026 · Final results</h2>
          <div className="historical-summary">
            <div>
              <span>CREDITED SALES</span>
              <strong>{dollars(periodData.sales)}</strong>
            </div>
            <div>
              <span>CREDITED ORDERS</span>
              <strong>{periodData.orders}</strong>
            </div>
            <div>
              <span>FINISHING RANK</span>
              <strong>#{periodData.rank} of 6</strong>
            </div>
            <div>
              <span>AVERAGE ORDER</span>
              <strong>{dollars(periodData.sales / periodData.orders)}</strong>
            </div>
          </div>
          <p style={{ marginTop: 12, fontSize: 11, color: '#71859b', lineHeight: 1.5 }}>
            Illustrative completed-month summary. Live history will use that month's results and
            standings.
          </p>
        </section>
      )}

      <section className="card history-comparison">
        <div className="card-head">
          <div>
            <span className="eyebrow">THE BIGGER PICTURE</span>
            <h3>Your momentum over time</h3>
          </div>
          <span className="small">Illustrative monthly sales</span>
        </div>
        <div className="history-grid">
          <div>
            <div className="trend-chart" role="region" aria-label="Monthly credited sales" tabIndex={0}>
            <div className="trend-bars" id="trend-bars">
              {MONTH_HISTORY.map((m, i) => (
                <div
                  key={m.label}
                  className={`month-column${i === periodData.index ? ' selected' : ''}${i === 5 ? ' partial' : ''}`}
                >
                  <strong>{dollars(m.sales)}</strong>
                  <i style={{ height: `${(m.sales / 22400) * 135}px` }} />
                  <span>
                    {m.label}
                    {i === 5 ? '*' : ''}
                  </span>
                </div>
              ))}
            </div>
            </div>
            <p className="history-note">
              Confirmed credited sales. September is partial through Sep 20.
            </p>
          </div>
          <div className="projection-panel">
            <span className="eyebrow" id="projection-kicker">
              {current
                ? 'SEPTEMBER MONTH-END PROJECTION'
                : `${periodData.month.toUpperCase()} FINAL RESULT`}
            </span>
            <h2 id="projection-value">{dollars(projValue)}</h2>
            <p id="projection-description">
              {current ? 'At the current daily pace' : 'Confirmed credited sales · Example'}
            </p>
            <div id="projection-comparisons">
              {earlierIdx.map((i) => {
                const old = MONTH_HISTORY[i];
                const pct = (projValue / old.sales - 1) * 100;
                return (
                  <div key={old.label}>
                    <span>
                      {current ? 'Projected vs.' : 'Compared with'} {old.label}
                    </span>
                    <strong>
                      {pct >= 0 ? '+' : ''}
                      {pct.toFixed(1)}%
                    </strong>
                  </div>
                );
              })}
            </div>
            <small id="projection-method">
              {current
                ? '$18,420 ÷ 20 elapsed days × 30 days. Simple run-rate estimate, not guaranteed revenue. Processing orders are excluded.'
                : 'Completed months show actual credited results, not forecasts.'}
            </small>
          </div>
        </div>
      </section>

      <div id="sales-order-section" className="sales-grid">
        <section className="card sales-orders">
          <div className="card-head">
            <div>
              <h3>Order attribution</h3>
              <p className="sales-sub">Recent examples from the selected period</p>
            </div>
            <span className="small">5 example orders</span>
          </div>
          <div className="sales-filters" role="group" aria-label="Order attribution filter">
            {(['all', 'credited', 'pending'] as Filter[]).map((key) => (
              <button
                key={key}
                type="button"
                data-status={key}
                aria-pressed={status === key}
                onClick={() => setStatus(key)}
              >
                {key === 'all' ? 'All examples' : key === 'credited' ? 'Credited' : 'Processing'}
              </button>
            ))}
          </div>
          <div className="sales-table-wrap" role="region" aria-label="Order attribution details" tabIndex={0}>
          <table className="sales-table">
            <thead>
              <tr>
                <th>Customer / order</th>
                <th>Order value</th>
                <th>Sales credited</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody id="sales-rows">
              {filteredRows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.customer}</strong>
                    <small>
                      {row.id} · {row.date}
                    </small>
                  </td>
                  <td>{money(row.value)}</td>
                  <td>{row.credit === null ? 'Pending' : money(row.credit)}</td>
                  <td>
                    <span className={row.status === 'credited' ? 'active-pill' : 'needs-pill'}>
                      {row.status === 'credited' ? 'Credited' : 'Processing'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="text-btn"
                      type="button"
                      onClick={() => setDialog({ kind: 'order', order: row })}
                    >
                      Details &rarr;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <div className="sales-table-note">
            Summary cards show the example period total. This table shows five recent examples.
          </div>
        </section>
        <aside className="sales-side">
          <section className="horse-prompt">
            <span className="eyebrow">YOUR NEXT MILESTONE</span>
            <h3>You're #3 on the team.</h3>
            <p>
              <strong>$540</strong> to match #2 this month. Keep taking care of your customers.
              Every credited sale moves your total forward.
            </p>
            <button
              className="outlined"
              id="show-pending"
              type="button"
              onClick={() => setStatus('pending')}
            >
              View system processing &rarr;
            </button>
          </section>
          <section className="card">
            <div className="section-heading">
              <h3>What these numbers mean</h3>
            </div>
            <div className="sales-explainer">
              <div>
                <strong>Credited sales</strong>
                <p>The system assigns sales credit using the approved attribution rules.</p>
              </div>
              <div>
                <strong>System processing</strong>
                <p>
                  Orders the system is still processing. They are excluded from your credited
                  totals.
                </p>
              </div>
              <div>
                <strong>System exceptions</strong>
                <p>
                  Unresolved exceptions appear in the Manager view. No attribution review is
                  required from you.
                </p>
              </div>
            </div>
          </section>
          <section className="card">
            <div className="section-heading">
              <h3>Your earnings are automatic</h3>
            </div>
            <div className="horse-related">
              <p>
                The system applies the compensation plan to qualifying sales. Pending orders and
                adjustments stay separate from earned compensation.
              </p>
              <button
                className="text-btn"
                id="earnings-side-link"
                type="button"
                onClick={() => setDialog({ kind: 'earnings' })}
              >
                View example calculation &rarr;
              </button>
            </div>
          </section>
        </aside>
      </div>

      {/* Dialogs */}
      <dialog
        className="horse-dialog"
        id="sales-dialog"
        ref={orderDialogRef}
        onClose={() => setDialog({ kind: 'closed' })}
      >
        <header>
          <h2>Order attribution</h2>
          <button
            id="sales-close"
            type="button"
            aria-label="Close"
            onClick={() => setDialog({ kind: 'closed' })}
          >
            &times;
          </button>
        </header>
        {dialog.kind === 'order' ? (
          <div className="sales-detail" id="sales-detail">
            <span className="eyebrow">{dialog.order.id} · EXAMPLE ORDER</span>
            <h2>{dialog.order.customer}</h2>
            <dl>
              <dt>Order value</dt>
              <dd>{money(dialog.order.value)}</dd>
              <dt>Credited to Jamie</dt>
              <dd>{dialog.order.credit === null ? 'Processing' : money(dialog.order.credit)}</dd>
              <dt>Status</dt>
              <dd>{dialog.order.status === 'credited' ? 'Credited' : 'System processing'}</dd>
            </dl>
            <p>{dialog.order.description}</p>
            <details className="issue-report">
              <summary>Report an issue</summary>
              <label>
                What looks incorrect?
                <textarea
                  id="review-notes"
                  placeholder="Describe the issue you noticed"
                />
              </label>
              <button className="blue-action" id="prepare-review" type="button">
                Report an issue
              </button>
              <p id="review-result" hidden>
                Issue report previewed only. Nothing was submitted and no attribution changed.
              </p>
            </details>
          </div>
        ) : null}
      </dialog>

      <dialog
        className="horse-dialog"
        id="earnings-dialog"
        ref={earningsDialogRef}
        onClose={() => setDialog({ kind: 'closed' })}
      >
        <header>
          <h2>How your bonus is calculated</h2>
          <button
            id="close-earnings"
            type="button"
            aria-label="Close calculation"
            onClick={() => setDialog({ kind: 'closed' })}
          >
            &times;
          </button>
        </header>
        <div className="sales-detail">
          <span className="eyebrow">DESIGN EXAMPLE ONLY · NOT YOUR APPROVED PLAN</span>
          <p>
            This example uses a monthly plan where the attained rate applies to all qualifying
            sales in that pay period.
          </p>
          <ul className="bonus-rule-list">
            <li>Tier 2 example rate: 5%.</li>
            <li>Tier 3 example threshold: $20,000 at 6%.</li>
            <li>The higher rate applies to the entire qualifying period total in this example.</li>
          </ul>
          <div className="bonus-example">
            Earned now: $18,420 × 5% = <strong>$921</strong>
            <br />
            At the next tier: $20,000 × 6% = <strong>$1,200</strong>
            <br />
            Additional qualifying sales needed: <strong>$1,580</strong>
            <br />
            Difference from earned bonus: <strong>$279</strong>
          </div>
          <p>
            The system calculates earned compensation under the configured plan. Pending
            attribution is excluded. The next-tier amount is a hypothetical milestone, not earned
            compensation.
          </p>
          <p>
            Rates, thresholds, qualifying-sales rules, adjustments, pay periods and payout dates
            shown here are placeholders for the approved plan.
          </p>
        </div>
      </dialog>

      <dialog
        className="horse-dialog"
        id="activity-coaching-dialog"
        ref={coachingDialogRef}
        onClose={() => setDialog({ kind: 'closed' })}
      >
        <header>
          <h2>Coaching context</h2>
          <button
            id="close-coaching-context"
            type="button"
            aria-label="Close"
            onClick={() => setDialog({ kind: 'closed' })}
          >
            &times;
          </button>
        </header>
        <div className="sales-detail">
          <span className="eyebrow">PRIVATE · ILLUSTRATIVE EVIDENCE</span>
          <p>
            Two completed conversations contain a product-use note but no customer goal in the
            corresponding profile.
          </p>
          <ul className="bonus-rule-list">
            <li>Example conversation A: product routine recorded; goal not recorded.</li>
            <li>Example conversation B: product question answered; goal not recorded.</li>
          </ul>
          <p>Try: "What are you hoping to see with your horse?"</p>
          <p>
            Missing notes do not prove the question was not asked. This is a prompt to check the
            context, not a performance judgment.
          </p>
        </div>
      </dialog>

      {/* Icon spacer to keep imports alive for linter. */}
      <span style={{ display: 'none' }} aria-hidden="true">
        <Icon id="chart" />
        {formatMoney(0)}
      </span>
    </WorkspaceChrome>
  );
}

export default MyActivityReviewScreen;

// ---------------------------------------------------------------------
// W37b: real-session view. Every number on this view is read off the
// composite body; a region without data says so.
// ---------------------------------------------------------------------

interface LiveActivityViewProps {
  rep: Rep | null;
  composite: UseMyActivityCompositeResult;
  returnToWork: () => void;
  updatesNote: string;
}

interface EnvelopeRead<T> {
  data: T | null;
  degraded: RegionEnvelope<unknown>['degraded'] | undefined;
  /** Server said {available: false, reason}: no source composed. */
  notAvailable: string | null;
  present: boolean;
}

function readLiveEnvelope<T>(env: RegionEnvelope<unknown> | undefined): EnvelopeRead<T> {
  if (!env) return { data: null, degraded: undefined, notAvailable: null, present: false };
  const raw = env.data;
  if (isRecord(raw) && raw.available === false) {
    const reason = typeof raw.reason === 'string' ? raw.reason : 'NOT_AVAILABLE';
    return { data: null, degraded: env.degraded, notAvailable: reason, present: true };
  }
  return { data: (raw as T | null) ?? null, degraded: env.degraded, notAvailable: null, present: true };
}

function notAvailableLabel(reason: string): string {
  if (reason === 'NO_COMPENSATION_SOURCE') return NO_COMPENSATION_LABEL;
  return `Not available yet (${reason})`;
}

function humanReason(reason: string): string {
  if (reason === 'NO_COMPENSATION_SOURCE') return 'no compensation source';
  if (reason === 'DEP_MISSING') return 'not wired on this deployment yet';
  if (reason === 'SUB_TIMEOUT') return 'source timed out';
  return reason;
}

function liveStatusOf(env: EnvelopeRead<unknown>): 'idle' | 'degraded' | 'live' {
  if (env.degraded) return 'degraded';
  if (env.data === null) return 'idle';
  return 'live';
}

function centsToDollars(cents: number | null | undefined): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return NO_DATA_PLACEHOLDER;
  return formatMoney(cents / 100);
}

function workdayLabel(workday: ActivityTodayRegion['workday'] | undefined): { state: string; detail: string } {
  if (!workday) return { state: NO_DATA_PLACEHOLDER, detail: 'No clock-in source connected yet.' };
  if (workday.state === 'clocked_in') {
    return {
      state: 'Clocked in',
      detail: workday.sessionStartMs
        ? `Started ${new Date(workday.sessionStartMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
        : 'Start time not recorded',
    };
  }
  if (workday.state === 'on_break') return { state: 'On break', detail: workday.activeBreak?.reason ?? 'Break in progress' };
  return { state: 'Not clocked in', detail: 'No clock-in source connected yet.' };
}

/** A card body for a region that is not available / degraded / missing. */
function RegionStatus({ env, testId }: { env: EnvelopeRead<unknown>; testId: string }): ReactNode {
  if (env.notAvailable) {
    return (
      <p data-testid={testId} className="activity-fine">
        {notAvailableLabel(env.notAvailable)}
      </p>
    );
  }
  if (env.degraded) {
    return (
      <>
        <p data-testid={testId} className="activity-fine">{NO_DATA_PLACEHOLDER}</p>
        <RegionDegradedNote degraded={env.degraded} />
      </>
    );
  }
  return (
    <p data-testid={testId} className="activity-fine">
      {NO_DATA_PLACEHOLDER}
    </p>
  );
}

function LiveActivityView({ rep, composite, returnToWork, updatesNote }: LiveActivityViewProps): ReactNode {
  const regions = composite.regions;
  const activityToday = readLiveEnvelope<ActivityTodayRegion>(regions['activity-today']);
  const conversion = readLiveEnvelope<ConversionRegion>(regions['conversion']);
  const customersHelped = readLiveEnvelope<CustomersHelpedRegion>(regions['customers-helped']);
  const retention = readLiveEnvelope<RetentionRegion>(regions['retention']);
  const responseTimes = readLiveEnvelope<ResponseTimesRegion>(regions['response-times']);
  const dailyPulse = readLiveEnvelope<DailyPulseRegion>(regions['daily-pulse']);
  const earnings = readLiveEnvelope<EarningsCardRegion>(regions['earnings-card']);
  const leaderboard = readLiveEnvelope<LeaderboardRegion>(regions['leaderboard-section']);
  const historical = readLiveEnvelope<HistoricalViewRegion>(regions['historical-view']);

  const repLabel = rep ? rep.fullName.toUpperCase() : 'YOUR ACTIVITY';
  const loading = composite.status === 'loading' && composite.data === null;
  const touches = activityToday.data?.touches ?? null;
  const workday = workdayLabel(activityToday.data?.workday);
  const letters = activityToday.data?.letters ?? null;
  const coaching = activityToday.data?.coaching ?? null;

  return (
    <WorkspaceChrome screen="my-activity" rail="my-activity">
      <div className="breadcrumbs">
        <div>
          Workspace
          <span className="slash">/</span>
          <strong>My Activity</strong>
        </div>
        <span className="preview" data-testid="my-activity-updates-note">{updatesNote}</span>
      </div>

      {composite.degraded.length > 0 ? (
        <div
          role="status"
          data-testid="my-activity-degraded-banner"
          className="my-activity-degraded-banner"
          style={{
            margin: '0 0 12px',
            padding: '10px 14px',
            background: '#fff5e5',
            border: '1px solid #ffe0b2',
            borderRadius: 8,
            fontSize: 12,
            color: '#8a4d00',
          }}
        >
          <strong>Partial data.</strong>{' '}
          {composite.degraded.length === 1
            ? 'One region is not available:'
            : `${composite.degraded.length} regions are not available:`}{' '}
          {composite.degraded.map((d, i) => (
            <span key={d.region}>
              {i > 0 ? ', ' : ''}
              <code>{d.region}</code>
              <small> ({humanReason(d.reason)})</small>
            </span>
          ))}
        </div>
      ) : null}

      {composite.status === 'error' && composite.error ? (
        <div
          role="alert"
          data-testid="my-activity-error-banner"
          style={{
            margin: '0 0 12px',
            padding: '10px 14px',
            background: '#fbeaea',
            border: '1px solid #e8bdbd',
            borderRadius: 8,
            fontSize: 12,
            color: '#8a1f1f',
          }}
        >
          Error loading activity: {composite.error.errorCode || 'UNKNOWN'}
        </div>
      ) : null}

      <section className="sales-hero">
        <div>
          <span className="eyebrow" data-testid="my-activity-rep">MY ACTIVITY · {repLabel}</span>
          <h1>Your work. Your impact.</h1>
          <p>
            {rep ? <span data-testid="my-activity-rep-email">{rep.email}</span> : 'Waiting for your HubSpot session.'}
            {' · '}Your conversations and customer outcomes, read from HubSpot.
          </p>
        </div>
        <div className="period-chip">
          <label htmlFor="sales-period-live">VIEW PERIOD</label>
          <strong id="sales-period-live">Today</strong>
          <span id="asof">
            {loading
              ? 'Loading today’s counts…'
              : composite.lastUpdatedMs
                ? `HubSpot engagements · UTC day · read ${new Date(composite.lastUpdatedMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
                : 'HubSpot engagements · UTC day'}
          </span>
        </div>
      </section>

      <section id="activity-today">
        <div className="activity-heading">
          <div>
            <span className="eyebrow">TODAY</span>
            <h2>Good conversations build great results.</h2>
          </div>
          <button className="back-to-work" type="button" onClick={returnToWork}>
            Return to My work &rarr;
          </button>
        </div>
        <div className="activity-metrics">
          <article className="card" data-region="activity-today">
            <span>CUSTOMER TOUCHES</span>
            {activityToday.degraded || !touches ? (
              <>
                <h2 data-testid="touches-value">{NO_DATA_PLACEHOLDER}</h2>
                {activityToday.degraded ? <RegionDegradedNote degraded={activityToday.degraded} /> : null}
              </>
            ) : (
              <>
                <h2 data-testid="touches-value">{touches.total}</h2>
                <p>
                  {touches.byType.call} calls · {touches.byType.text} texts · {touches.byType.email} emails ·{' '}
                  {touches.byType.note} notes
                </p>
                <small>HubSpot engagements you created today</small>
              </>
            )}
          </article>

          <article className="card" data-region="conversion">
            <span>CONVERSION TODAY</span>
            {conversion.notAvailable ? (
              <>
                <h2 data-testid="conversion-value">{NO_DATA_PLACEHOLDER}</h2>
                <small data-testid="conversion-not-available">{notAvailableLabel(conversion.notAvailable)}</small>
              </>
            ) : conversion.degraded ? (
              <>
                <h2 data-testid="conversion-value">{NO_DATA_PLACEHOLDER}</h2>
                <RegionDegradedNote degraded={conversion.degraded} />
              </>
            ) : (
              <>
                <h2 data-testid="conversion-value">
                  {conversion.data?.ratio !== null && conversion.data?.ratio !== undefined
                    ? Math.round(conversion.data.ratio * 100)
                    : NO_DATA_PLACEHOLDER}
                  {conversion.data?.ratio !== null && conversion.data?.ratio !== undefined ? <span>%</span> : null}
                </h2>
                {conversion.data && conversion.data.ratio !== null && conversion.data.sevenDayAverage !== null ? (
                  <p>
                    <b>{formatPercentDelta(conversion.data.ratio, conversion.data.sevenDayAverage) ?? ''}</b>
                  </p>
                ) : null}
                {conversion.data && conversion.data.closes !== null && conversion.data.contactsWorked !== null ? (
                  <small>
                    {conversion.data.closes} of {conversion.data.contactsWorked} eligible conversations
                  </small>
                ) : null}
              </>
            )}
          </article>

          <article className="card" data-region="customers-helped">
            <span>CUSTOMERS HELPED</span>
            {customersHelped.degraded ? (
              <>
                <h2 data-testid="customers-helped-value">{NO_DATA_PLACEHOLDER}</h2>
                <RegionDegradedNote degraded={customersHelped.degraded} />
              </>
            ) : (
              <>
                <h2 data-testid="customers-helped-value">
                  {customersHelped.data?.count ?? NO_DATA_PLACEHOLDER}
                </h2>
                <p>Distinct customers with completed actions</p>
                <small>Repeat touches count once per customer</small>
              </>
            )}
          </article>

          <article className="card" data-region="retention">
            <span>RETENTION THIS WEEK</span>
            {retention.notAvailable ? (
              <>
                <h2 data-testid="retention-value">{NO_DATA_PLACEHOLDER}</h2>
                <small>{notAvailableLabel(retention.notAvailable)}</small>
              </>
            ) : retention.degraded || !retention.data ? (
              <>
                <h2 data-testid="retention-value">{NO_DATA_PLACEHOLDER}</h2>
                {retention.degraded ? <RegionDegradedNote degraded={retention.degraded} /> : null}
              </>
            ) : (
              <>
                <h2 data-testid="retention-value">
                  {retention.data.confirmed ?? NO_DATA_PLACEHOLDER}{' '}
                  {retention.data.confirmed !== null ? <span>confirmed</span> : null}
                </h2>
                {retention.data.pending !== null || retention.data.notRetained !== null ? (
                  <p>
                    {(retention.data.pending ?? 0) + (retention.data.confirmed ?? 0) + (retention.data.notRetained ?? 0)} actions ·{' '}
                    {retention.data.pending ?? 0} outcomes pending · {retention.data.notRetained ?? 0} not retained
                  </p>
                ) : null}
                <small>Confirmed outcomes, not just messages sent</small>
              </>
            )}
          </article>
        </div>

        <div className="activity-detail-grid">
          <section className="card" data-region="response-times">
            <div className="card-head">
              <h3>Your response time today</h3>
              <span className="small">Median response times</span>
            </div>
            {responseTimes.degraded || !responseTimes.data ? (
              <>
                <div className="response-rows" data-testid="response-times-degraded">
                  {(['Calls', 'Live chat', 'Text', 'Email'] as const).map((label, i) => (
                    <div key={label}>
                      <span>
                        <i className={`channel-dot ${['call', 'chat', 'text', 'email'][i]}`} /> {label}
                      </span>
                      <strong>{NO_DATA_PLACEHOLDER}</strong>
                    </div>
                  ))}
                </div>
                {responseTimes.degraded ? <RegionDegradedNote degraded={responseTimes.degraded} /> : null}
              </>
            ) : (
              <div className="response-rows">
                <div>
                  <span>
                    <i className="channel-dot call" /> Calls
                  </span>
                  <strong>{formatSeconds(responseTimes.data.calls)}</strong>
                  <small>{responseTimes.data.counts.calls} answered inbound calls</small>
                </div>
                <div>
                  <span>
                    <i className="channel-dot chat" /> Live chat
                  </span>
                  <strong>{formatSeconds(responseTimes.data.chat)}</strong>
                  <small>{responseTimes.data.counts.chat} first responses</small>
                </div>
                <div>
                  <span>
                    <i className="channel-dot text" /> Text
                  </span>
                  <strong>{formatSeconds(responseTimes.data.text)}</strong>
                  <small>{responseTimes.data.counts.text} first responses</small>
                </div>
                <div>
                  <span>
                    <i className="channel-dot email" /> Email
                  </span>
                  <strong>{formatSeconds(responseTimes.data.email)}</strong>
                  <small>{responseTimes.data.counts.email} first responses</small>
                </div>
              </div>
            )}
            <p className="activity-fine">
              Calls measure answer time. Messages measure time to first rep response. Not averaged
              across channels.
            </p>
          </section>
          <section className="card workday-card" data-testid="my-activity-workday">
            <div className="card-head">
              <h3>Your workday</h3>
              <span className="small">Clock-in state</span>
            </div>
            <div className="workday-hours">
              <div>
                <span>State</span>
                <strong>{workday.state}</strong>
              </div>
            </div>
            <p className="activity-fine">{workday.detail}</p>
          </section>
          <section className="coaching-nudge" data-testid="my-activity-coaching">
            <span className="eyebrow">A SMALL THING TO TRY</span>
            {coaching && coaching.text ? (
              <>
                <h3>{coaching.text}</h3>
                <small>Private coaching{coaching.patternId ? ` · ${coaching.patternId}` : ''}</small>
              </>
            ) : (
              <>
                <h3>No coaching signal yet.</h3>
                <small>Coaching suggestions appear here once a coaching source is connected.</small>
              </>
            )}
          </section>
        </div>
        <div className="activity-work-strip">
          <div>
            <span className="work-avatar">
              <Icon id="work" />
            </span>
            <div>
              <strong>Your current work</strong>
              <p>
                Your prioritized queue stays in My work. Finish one customer, then move to the next.
              </p>
            </div>
          </div>
          <button className="back-to-work" type="button" onClick={returnToWork}>
            My work &rarr;
          </button>
          <div className="letter-status" data-testid="my-activity-letters">
            <span>END-OF-DAY LETTERS</span>
            {letters ? (
              <>
                <strong>{letters.total} today</strong>
                <small>
                  {letters.drafted} drafted · {letters.sent} sent
                </small>
              </>
            ) : (
              <>
                <strong>{NO_DATA_PLACEHOLDER}</strong>
                <small>Completed count appears when connected.</small>
              </>
            )}
          </div>
        </div>
      </section>

      <section
        className="daily-pulse"
        id="daily-pulse"
        data-live-status={liveStatusOf(dailyPulse)}
        data-testid="daily-pulse-live-status"
      >
        <div className="pulse-heading">
          <div>
            <span className="eyebrow">SALES MOMENTUM</span>
            <h2>Your sales results today.</h2>
          </div>
        </div>
        <div className="daily-cards">
          <article>
            <span>SALES CREDITED TODAY</span>
            <h2>{NO_DATA_PLACEHOLDER}</h2>
            <small data-testid="sales-today-not-available">{NO_COMPENSATION_LABEL}</small>
          </article>
          <article>
            <span>DAILY PULSE</span>
            {dailyPulse.data ? (
              <>
                <h2>{dailyPulse.data.touches?.total ?? NO_DATA_PLACEHOLDER}</h2>
                <small>
                  touches · calls in {dailyPulse.data.calls?.inbound ?? NO_DATA_PLACEHOLDER} · out{' '}
                  {dailyPulse.data.calls?.outbound ?? NO_DATA_PLACEHOLDER}
                </small>
              </>
            ) : (
              <RegionStatus env={dailyPulse} testId="daily-pulse-status" />
            )}
          </article>
        </div>
      </section>

      <section
        className="earnings-card"
        id="earnings-card"
        data-live-status={liveStatusOf(earnings)}
        data-testid="earnings-card-live-status"
      >
        <div className="earnings-header">
          <div>
            <span className="eyebrow">YOUR EARNINGS</span>
            <h2>See what your momentum earns.</h2>
          </div>
        </div>
        <div className="earnings-grid">
          {earnings.data ? (
            <div className="earned-total">
              <span>BONUS EARNED THIS PAY PERIOD</span>
              <h2>{centsToDollars(earnings.data.bonus?.bonusCents)}</h2>
              <p>
                {centsToDollars(earnings.data.bonus?.qualifyingCents)} in qualifying sales
              </p>
              <div className="payout-line">
                <span>Next payout</span>
                <strong>
                  {earnings.data.nextPayoutMs
                    ? new Date(earnings.data.nextPayoutMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : NO_DATA_PLACEHOLDER}
                </strong>
              </div>
            </div>
          ) : (
            <div className="earned-total">
              <span>BONUS EARNED THIS PAY PERIOD</span>
              <h2>{NO_DATA_PLACEHOLDER}</h2>
              <RegionStatus env={earnings} testId="earnings-status" />
            </div>
          )}
        </div>
      </section>

      <section
        id="leaderboard-section"
        className="card leaderboard"
        data-live-status={liveStatusOf(leaderboard)}
        data-testid="leaderboard-live-status"
      >
        <div className="card-head">
          <div>
            <span className="eyebrow">TEAM LEADERBOARD</span>
            <h3>A little friendly competition.</h3>
          </div>
        </div>
        <div className="leaderboard-content">
          {leaderboard.data && leaderboard.data.rows.length > 0 ? (
            <div className="rank-list">
              <div className="rank-row rank-label">
                <span>RANK / REP</span>
                <span>CREDITED SALES</span>
              </div>
              {leaderboard.data.rows.map((row) => (
                <div key={row.repId} className={`rank-row${row.isSelf ? ' your-rank' : ''}`}>
                  <div>
                    <b className="rank-medal">{row.rank}</b>
                    <span>
                      {row.displayName ?? row.repId}
                      {row.isSelf ? <em>YOU</em> : null}
                    </span>
                  </div>
                  <strong>{centsToDollars(row.creditedCents)}</strong>
                </div>
              ))}
            </div>
          ) : (
            <div className="rank-summary">
              <span className="eyebrow">YOUR POSITION</span>
              <RegionStatus env={leaderboard} testId="leaderboard-status" />
            </div>
          )}
        </div>
      </section>

      <div id="live-period-view">
        <div className="sales-metrics">
          <article className="card">
            <span>CREDITED SALES</span>
            <h2>{NO_DATA_PLACEHOLDER}</h2>
            <p data-testid="credited-sales-not-available">{NO_COMPENSATION_LABEL}</p>
          </article>
          <article className="card">
            <span>CREDITED ORDERS</span>
            <h2>{NO_DATA_PLACEHOLDER}</h2>
            <p>{NO_COMPENSATION_LABEL}</p>
          </article>
        </div>
      </div>

      <section
        className="card history-comparison"
        id="historical-view"
        data-live-status={liveStatusOf(historical)}
        data-testid="historical-view-live-status"
      >
        <div className="card-head">
          <div>
            <span className="eyebrow">THE BIGGER PICTURE</span>
            <h3>Your momentum over time</h3>
          </div>
        </div>
        {historical.data && historical.data.months.length > 0 ? (
          <ul>
            {historical.data.months.map((m) => (
              <li key={m.startMs}>
                {new Date(m.startMs).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}:{' '}
                {centsToDollars(m.creditedCents)}
                {m.partial ? ' (partial)' : ''}
              </li>
            ))}
          </ul>
        ) : (
          <RegionStatus env={historical} testId="historical-status" />
        )}
      </section>

      <div id="sales-order-section" className="sales-grid">
        <section className="card sales-orders">
          <div className="card-head">
            <div>
              <h3>Order attribution</h3>
              <p className="sales-sub">Orders credited to you</p>
            </div>
          </div>
          <p className="sales-table-note" data-testid="attribution-not-available">
            {NO_COMPENSATION_LABEL}
          </p>
        </section>
        <aside className="sales-side">
          <section className="card">
            <div className="section-heading">
              <h3>What these numbers mean</h3>
            </div>
            <div className="sales-explainer">
              <div>
                <strong>Customer touches</strong>
                <p>HubSpot calls, notes, tasks, texts and emails you created today (UTC day).</p>
              </div>
              <div>
                <strong>Customers helped</strong>
                <p>Distinct contacts on those touches.</p>
              </div>
              <div>
                <strong>Credited sales and earnings</strong>
                <p>Appear once a compensation and attribution source is connected.</p>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </WorkspaceChrome>
  );
}

interface RegionDegradedNoteProps {
  degraded: NonNullable<RegionEnvelope<unknown>['degraded']>;
}

function RegionDegradedNote({ degraded }: RegionDegradedNoteProps): ReactNode {
  return (
    <small
      data-testid="region-degraded-note"
      style={{ display: 'block', marginTop: 4, color: '#8a4d00' }}
    >
      {degraded.note || `Temporarily unavailable (${degraded.reason}).`}
    </small>
  );
}
